import type { ApiHook } from '@frontend/api-client';
import type { getMutationHandler } from '@frontend/duck-tape';
import type { TypedSegmentTrack } from '@frontend/react';
import type { PaymentStepDependencies } from '@frontend/react-domain';
import type { TypedAdvertisingTrack } from '@frontend/react/hooks/getUseAdvertisingHook';
import { useTrackingSlice } from '@frontend/redux';
import { useWebStripeCard, type WebStripeCardState } from './useWebStripeCard';

/**
 * Web dependencies that need to be injected from the web app to avoid circular imports
 */
export type WebPaymentDependencies = {
  // Utility from @frontend/web/utils
  handleMutation: ReturnType<typeof getMutationHandler>;
  // Analytics tracking function
  track: TypedSegmentTrack;
  trackAdvertising: TypedAdvertisingTrack;
  // API hooks from @frontend/web/hooks
  useBuyCreditsMutation: ApiHook<'useBuyCreditsMutation'>;
  useCreateSetupIntentMutation: ApiHook<'useCreateSetupIntentMutation'>;
  useLazyListCardsQuery: ApiHook<'useLazyListCardsQuery'>;
  useRetrieveClientQuery: ApiHook<'useRetrieveClientQuery'>;
  useSetupIntentConfirmedMutation: ApiHook<'useSetupIntentConfirmedMutation'>;
  useUpdatePlanMutation: ApiHook<'useUpdatePlanMutation'>;
  // Stripe state from useStripeCard hook
  webStripeCard: WebStripeCardState;
};

/**
 * Web platform implementation of complete payment step dependencies.
 *
 * Receives web-specific dependencies via dependency injection to avoid circular imports.
 * Combines API dependencies, utilities, and Stripe dependencies into a single
 * dependency injection interface for cross-platform payment functionality.
 *
 * @param webDeps - Web-specific dependencies injected from the web app
 * @returns Complete PaymentStepDependencies for web platform
 */
export const useWebPaymentDependencies = (webDeps: WebPaymentDependencies): PaymentStepDependencies => {
  const {
    handleMutation,
    track,
    trackAdvertising,
    useBuyCreditsMutation,
    useCreateSetupIntentMutation,
    useLazyListCardsQuery,
    useRetrieveClientQuery,
    useSetupIntentConfirmedMutation,
    useUpdatePlanMutation,
    webStripeCard,
  } = webDeps;

  // Web-specific Stripe dependencies using injected stripe state
  const stripe = useWebStripeCard(webStripeCard);

  return {
    // Web-specific API dependencies - actual hooks injected from web app
    api: {
      useBuyCreditsMutation,
      useCreateSetupIntentMutation,
      useLazyListCardsQuery,
      useRetrieveClientQuery,
      useSetupIntentConfirmedMutation,
      useUpdatePlanMutation,
    },

    // Web-specific Stripe dependencies
    stripe,

    // Web-specific utility dependencies
    utilities: {
      handleMutation,
      track,
      trackAdvertising,
      useTrackingSlice,
    },
  };
};
